import { useEffect, useState } from "react";
import { DownloadSimple, ChartBar } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, money, fmtDate } from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { useI18n } from "@/lib/i18n";

const KPI = ({ label, value, tone = "amber" }) => (
  <div className="p-5 bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
    <div className="text-[10px] uppercase font-mono tracking-widest text-steel">{label}</div>
    <div className={`font-mono text-3xl font-bold mt-2 ${
      tone === "green" ? "text-emerald-600" : tone === "red" ? "text-red-600" : "text-zinc-900 dark:text-deep-space"
    }`}>{value}</div>
  </div>
);

export default function Reports() {
  const { t } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [range, setRange] = useState({ from_date: monthAgo, to_date: today });
  const [revenue, setRevenue] = useState(null);
  const [mechanics, setMechanics] = useState([]);
  const [parts, setParts] = useState(null);

  const loadAll = () => {
    api.get("/reports/revenue", { params: range }).then((r) => setRevenue(r.data));
    api.get("/reports/mechanics").then((r) => setMechanics(r.data));
    api.get("/reports/parts").then((r) => setParts(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const exportRevenue = () => {
    if (!revenue) return;
    const rows = (revenue.invoices || []).map((i) => ({
      invoice_number: i.invoice_number, date: (i.created_at || "").slice(0, 10),
      subtotal: i.subtotal, tax: i.tax_amount, total: i.total,
      paid: i.paid_amount, balance: i.balance, status: i.status,
    }));
    downloadCsv(rows, ["invoice_number","date","subtotal","tax","total","paid","balance","status"], `revenue_${range.from_date}_${range.to_date}.csv`);
  };
  const exportMechanics = () => {
    downloadCsv(mechanics.map((m) => ({
      name: m.name, role: m.role, tasks: m.tasks, completed: m.completed,
      hours_est: m.hours_est.toFixed(2), hours_actual: m.hours_actual.toFixed(2),
    })), ["name","role","tasks","completed","hours_est","hours_actual"], `mechanics.csv`);
  };
  const exportParts = () => {
    if (!parts) return;
    const rows = [
      ...(parts.low_stock || []).map((p) => ({ scope: "low_stock", sku: p.sku, name: p.name, brand: p.brand, category: p.category, stock_qty: p.stock_qty, reorder_level: p.reorder_level, price: p.price })),
      ...(parts.top_stocked || []).map((p) => ({ scope: "top_stocked", sku: p.sku, name: p.name, brand: p.brand, category: p.category, stock_qty: p.stock_qty, reorder_level: p.reorder_level, price: p.price })),
    ];
    downloadCsv(rows, ["scope","sku","name","brand","category","stock_qty","reorder_level","price"], `parts.csv`);
  };

  return (
    <div className="space-y-6" data-testid="reports-root">
      <div>
        <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-1">Insights</div>
        <h1 className="text-3xl font-display font-bold">{t("reports_title")}</h1>
      </div>

      <Tabs defaultValue="revenue" className="space-y-4">
        <TabsList className="rounded-none bg-transparent border-b border-frosted/50 dark:border-frosted/50 w-full justify-start h-auto p-0">
          <TabsTrigger data-testid="tab-rep-revenue" value="revenue" className="rounded-none border-b-2 border-transparent data-[state=active]:border-strawberry data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-3 font-mono uppercase text-xs tracking-widest">{t("rep_revenue")}</TabsTrigger>
          <TabsTrigger data-testid="tab-rep-mechanics" value="mechanics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-strawberry data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-3 font-mono uppercase text-xs tracking-widest">{t("rep_mechanics")}</TabsTrigger>
          <TabsTrigger data-testid="tab-rep-parts" value="parts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-strawberry data-[state=active]:bg-transparent data-[state=active]:shadow-none px-5 py-3 font-mono uppercase text-xs tracking-widest">{t("rep_parts")}</TabsTrigger>
        </TabsList>

        <TabsContent value="revenue" className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>From</Label>
              <Input data-testid="rep-from" type="date" value={range.from_date}
                onChange={(e) => setRange({ ...range, from_date: e.target.value })}
                className="rounded-none h-10 mt-1 font-mono" />
            </div>
            <div>
              <Label>To</Label>
              <Input data-testid="rep-to" type="date" value={range.to_date}
                onChange={(e) => setRange({ ...range, to_date: e.target.value })}
                className="rounded-none h-10 mt-1 font-mono" />
            </div>
            <Button data-testid="rep-apply-btn" onClick={loadAll}
              className="h-10 btn-primary-brand rounded-md text-white font-semibold">Apply</Button>
            <Button data-testid="rep-revenue-csv" variant="outline" onClick={exportRevenue}
              className="h-10 rounded-none border-frosted/60 dark:border-frosted/60 ml-auto">
              <DownloadSimple size={16} className="mr-1.5" weight="duotone" /> {t("rep_export_csv")}
            </Button>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <KPI label="Invoices" value={revenue?.invoice_count ?? "—"} />
            <KPI label="Billed (₹)" value={money(revenue?.total_billed ?? 0)} tone="green" />
            <KPI label="Paid (₹)" value={money(revenue?.total_paid ?? 0)} tone="green" />
            <KPI label="Outstanding (₹)" value={money(revenue?.total_outstanding ?? 0)} tone="red" />
          </div>

          <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
            <div className="p-4 border-b border-frosted/50 dark:border-frosted/50 flex items-center gap-2">
              <ChartBar size={18} className="text-strawberry" weight="duotone" />
              <div className="font-display font-semibold">Daily billing</div>
            </div>
            <div className="p-5 space-y-2">
              {(revenue?.by_day || []).length === 0 && <div className="text-sm text-steel">No invoices in this range.</div>}
              {(revenue?.by_day || []).map((row) => {
                const max = Math.max(...(revenue?.by_day || []).map((r) => r.billed || 1));
                const w = Math.round(((row.billed || 0) / (max || 1)) * 100);
                return (
                  <div key={row.date} className="grid grid-cols-12 items-center gap-3 text-sm">
                    <div className="col-span-2 font-mono text-xs text-steel">{row.date}</div>
                    <div className="col-span-8 h-6 bg-honeydew dark:bg-deep-space relative">
                      <div className="absolute inset-y-0 left-0 bg-strawberry" style={{ width: `${w}%` }} />
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm">₹ {money(row.billed)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="mechanics" className="space-y-4">
          <div className="flex justify-end">
            <Button data-testid="rep-mech-csv" variant="outline" onClick={exportMechanics}
              className="h-10 rounded-none border-frosted/60 dark:border-frosted/60">
              <DownloadSimple size={16} className="mr-1.5" weight="duotone" /> {t("rep_export_csv")}
            </Button>
          </div>
          <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
            <table className="w-full table-industrial">
              <thead><tr><th>Name</th><th>Role</th><th className="text-right">Tasks</th><th className="text-right">Done</th><th className="text-right">Est hrs</th><th className="text-right">Actual hrs</th></tr></thead>
              <tbody>
                {mechanics.length === 0 && (<tr><td colSpan={6} className="text-center py-8 text-steel">No data.</td></tr>)}
                {mechanics.map((m) => (
                  <tr key={m.user_id}>
                    <td>{m.name}</td>
                    <td className="text-xs uppercase font-mono">{m.role}</td>
                    <td className="text-right font-mono">{m.tasks}</td>
                    <td className="text-right font-mono text-emerald-500">{m.completed}</td>
                    <td className="text-right font-mono">{m.hours_est.toFixed(1)}</td>
                    <td className="text-right font-mono">{m.hours_actual.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="parts" className="space-y-4">
          <div className="flex justify-end">
            <Button data-testid="rep-parts-csv" variant="outline" onClick={exportParts}
              className="h-10 rounded-none border-frosted/60 dark:border-frosted/60">
              <DownloadSimple size={16} className="mr-1.5" weight="duotone" /> {t("rep_export_csv")}
            </Button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <KPI label="Total SKUs" value={parts?.total_skus ?? "—"} />
            <KPI label="Inventory value (₹)" value={money(parts?.inventory_value ?? 0)} tone="green" />
            <KPI label="Low-stock" value={parts?.low_stock_count ?? "—"} tone="red" />
          </div>
          <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
            <div className="p-4 border-b border-frosted/50 dark:border-frosted/50 font-display font-semibold">Low-stock parts</div>
            <table className="w-full table-industrial">
              <thead><tr><th>SKU</th><th>Name</th><th className="text-right">Stock</th><th className="text-right">Reorder at</th></tr></thead>
              <tbody>
                {(parts?.low_stock || []).length === 0 && (<tr><td colSpan={4} className="text-center py-8 text-steel">All good — nothing below reorder level.</td></tr>)}
                {(parts?.low_stock || []).map((p) => (
                  <tr key={p.id}>
                    <td className="font-mono text-strawberry">{p.sku}</td>
                    <td>{p.name}</td>
                    <td className="text-right font-mono text-red-500">{p.stock_qty}</td>
                    <td className="text-right font-mono">{p.reorder_level}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
